import { FC } from "react";
import styled from "@emotion/styled";
import { Typography } from "@kaltura/ds-react-components";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery, useTranslateTime } from "@kaltura/mediaspace-shared-utils";
import { ChevronDown16Icon, ChevronRight16Icon } from "@kaltura/ds-react-icons";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { TimeDisplay } from "@kaltura/mediaspace-shared-types";

export interface HeroUnitDatesProps {
    className?: string;
    startTime: number;
    endTime?: number;
}

// Horizontal layout on most of the devices, vertical layout on extra-small
const verticalLayoutBreakpoint = "sm";

const StyledDatesContainer = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: theme.spacing(2),
    flexDirection: "column",
    [theme.breakpoints.up(verticalLayoutBreakpoint)]: {
        flexDirection: "row",
    },
}));

/**
 * show a from - to time range.
 * format is determined by context.
 * @param className
 * @param startTime unix timestamp, seconds.
 * @param endTime unix timestamp, seconds.
 * @constructor
 */
const Dates: FC<HeroUnitDatesProps> = ({ className, startTime, endTime = 0 }) => {
    const context = useConfig();
    const { timeDisplay, dateFormats } = context.application;
    const timeFormat = `${dateFormats.shortDate} ${timeDisplay === TimeDisplay.TwentyFourHours ? "H:mm" : "h:mm A"}`;

    const withTz = `${timeFormat} z`;
    // format end time with tz
    const formattedEndTime = useTranslateTime(endTime, withTz);
    // if no end time, start time should have tz
    const formattedStartTime = useTranslateTime(startTime, formattedEndTime ? timeFormat : withTz);

    const theme = useTheme();
    const isHorizontalLayout = useMediaQuery(theme.breakpoints.up(verticalLayoutBreakpoint));

    return (
        <StyledDatesContainer className={className} variant={"h5"} component={"div"}>
            <Box className="kms-ds-hero-date-start">{formattedStartTime}</Box>
            {formattedEndTime && (
                <>
                    {isHorizontalLayout ? <ChevronRight16Icon /> : <ChevronDown16Icon />}
                    <Box className="kms-ds-hero-date-end">{formattedEndTime}</Box>
                </>
            )}
        </StyledDatesContainer>
    );
};

export default Dates;
